import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Box,
    Button,
    HStack,
    Link,
    VStack,
    Text,
    Spinner,
    IconButton,
} from "@chakra-ui/react";
import {
    HiOutlineCpuChip, HiStar,
    HiPhone, HiEnvelope, HiClock, HiArrowPath, HiOutlineShieldCheck,
} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { CandidateService } from "@/services/CandidateService";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import CandidateStatusBadge from "./components/CandidateStatusBadge";
import CandidateAiDataTab from "./components/candidate/CandidateAiDataTab";
import CandidateEvaluationTab from "./components/candidate/CandidateEvaluationTab";
import CandidateTimelineTab from "./components/candidate/CandidateTimelineTab";
import CandidateNotesTab from "./components/candidate/CandidateNotesTab";
import CandidateAttachmentsTab from "./components/candidate/CandidateAttachmentsTab";
import CandidateInteractionsTab from "./components/candidate/CandidateInteractionsTab";
import useCandidateSingleActions from "./hooks/useCandidateSingleActions";
import { ScoreBadge, evaluationStatusFromApi, getScoreBucket, SCORING_ENABLED } from "@/scoringUi";

const SCORE_CIRC = 2 * Math.PI * 28;

const CandidateDetailPage = () => {
    const { t } = useTranslation("models");
    const { recruitmentId, candidateId } = useParams();
    const location = useLocation();

    const [candidate, setCandidate] = useState(null);
    const [notes, setNotes] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [interactionsTotalCount, setInteractionsTotalCount] = useState(0);
    const [latestConsent, setLatestConsent] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isNotesLoading, setIsNotesLoading] = useState(true);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesSortDirection, setNotesSortDirection] = useState('desc');
    const [debouncedNotesSearch, setDebouncedNotesSearch] = useState('');
    const [notesFetchKey, setNotesFetchKey] = useState(0);
    const [timelineFetchKey, setTimelineFetchKey] = useState(0);
    const [isTimelineLoading, setIsTimelineLoading] = useState(true);
    const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(location.state?.tab ?? "ai");
    const [highlightId, setHighlightId] = useState(location.state?.highlightId ?? null);

    const { openStatusModal, openEmailModal, openNoteModal, openTimelineModal, modals: singleActionModals } = useCandidateSingleActions({
        t,
        onStatusChanged: (_, updated) => setCandidate(prev => ({ ...prev, status: updated.status })),
        onNoteAdded: () => setNotesFetchKey(k => k + 1),
        onTimelineEntryAdded: () => setTimelineFetchKey(k => k + 1),
    });

    useSetPageHeader(t("candidateDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
        CandidateService.getById(candidateId).then(setCandidate).finally(() => setIsLoading(false));
    }, [candidateId]);

    useEffect(() => {
        const timer = setTimeout(() => { setDebouncedNotesSearch(notesSearch); }, 400);
        return () => clearTimeout(timer);
    }, [notesSearch]);

    useEffect(() => {
        setIsNotesLoading(true);
        CandidateService.getNotes(candidateId, 'createdAt', notesSortDirection, debouncedNotesSearch || null)
            .then(d => setNotes(d ?? []))
            .finally(() => setIsNotesLoading(false));
    }, [candidateId, notesSortDirection, debouncedNotesSearch, notesFetchKey]);

    useEffect(() => {
        setIsTimelineLoading(true);
        CandidateService.getTimeline(candidateId).then(d => setTimeline(d ?? [])).finally(() => setIsTimelineLoading(false));
    }, [candidateId, timelineFetchKey]);

    useEffect(() => {
        setIsAttachmentsLoading(true);
        CandidateService.getAttachments(candidateId).then(d => setAttachments(d ?? [])).finally(() => setIsAttachmentsLoading(false));
    }, [candidateId]);

    useEffect(() => {
        setLatestConsent(undefined);
        CandidateService.getConsents(candidateId)
            .then(d => setLatestConsent(Array.isArray(d) && d.length ? d[0] : null))
            .catch(() => setLatestConsent(null));
    }, [candidateId]);

    useEffect(() => {
        CandidateService.getInteractions(candidateId, 1, 1).then(d => setInteractionsTotalCount(d?.totalCount ?? 0));
    }, [candidateId]);

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await CandidateService.toggleFavorite(candidateId);
        setCandidate(prev => ({ ...prev, isFavourite: updated.isFavourite }));
        setIsFavLoading(false);
    };


    const handleOpenAttachment = (att) => {
        if (!att.path) return;
        const url = `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Candidate/${candidateId}/attachments/file?name=${encodeURIComponent(att.path)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadAttachment = async (att) => {
        if (!att.path) return;
        try {
            const response = await CandidateService.downloadAttachment(candidateId, att.path);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = att.name ?? att.path.split('/').pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        }
    };

    const handleUploadAttachments = async (files) => {
        const newAtts = await CandidateService.addAttachments(candidateId, files);
        setAttachments(prev => [...prev, ...(newAtts ?? [])]);
    };

    const handleDownloadAllZip = async () => {
        try {
            const response = await CandidateService.downloadAllAttachmentsZip(candidateId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `attachments_${candidateId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        }
    };

    const handleExportXlsx = async () => {
        if (!recruitmentId) return;
        setIsXlsxLoading(true);
        try {
            const response = await CandidateService.exportXlsx(recruitmentId, [candidateId]);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kandydat_${candidate.firstname}_${candidate.lastname}-${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleHeaderAddNote = () => openNoteModal(candidateId);

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (!candidate) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">{t("candidateDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    const fullName = [candidate.firstname, candidate.lastname].filter(Boolean).join(" ") || "-";
    const score = candidate.evaluation?.scorePercent ?? null;
    const evaluationStatus = candidate.evaluation ? evaluationStatusFromApi(candidate.evaluation.status) : null;
    const scoreOffset = score != null ? SCORE_CIRC - (Math.min(100, Math.max(0, score)) / 100) * SCORE_CIRC : SCORE_CIRC;
    const scoreBucket = score != null ? getScoreBucket({ scorePercent: score, status: evaluationStatus }) : null;
    const scoreBucketColor = scoreBucket?.color ?? "brand.400";
    const scoreRingColor = `var(--chakra-colors-${scoreBucketColor.replace(".", "-")}, #1aa5ff)`;

    const TABS = [
        { key: "ai", label: t("candidateDetailPage.tabAiData") },
        { key: "timeline", label: t("candidateDetailPage.tabTimeline"), count: timeline.length },
        ...(SCORING_ENABLED ? [{ key: "evaluation", label: t("candidateDetailPage.tabEvaluation") }] : []),
        { key: "notes", label: t("candidateDetailPage.tabNotes"), count: notes.length },
        { key: "attachments", label: t("candidateDetailPage.tabAttachments"), count: attachments.length },
        { key: "interactions", label: t("candidateDetailPage.tabInteractions"), count: interactionsTotalCount },
    ];

    return (
        <>
            <VStack w="full" py={6} gap={4} align="flex-start">

                {/* Header card */}
                <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        {/* Left: name + contact info */}
                        <VStack align="flex-start" gap={2} flex={1} minW="200px">
                            <HStack gap={2}>
                                <Text fontWeight={700} fontSize="2xl" color="white">{fullName}</Text>
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Ulubione"
                                    color={candidate.isFavourite ? "yellow.400" : "gray.500"}
                                    loading={isFavLoading}
                                    onClick={handleToggleFavorite}
                                >
                                    <HiStar />
                                </IconButton>
                            </HStack>
                            <Box
                                as="button"
                                cursor="pointer"
                                transition="opacity 0.15s"
                                onClick={() => openStatusModal(candidateId, candidate.status)}
                                outline="none"
                            >
                                <CandidateStatusBadge status={candidate.status} />
                            </Box>
                            {candidate.phoneNumber && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiPhone /></Box>
                                    <Link href={`tel:${candidate.phoneNumber}`} color="white" fontSize="sm">{candidate.phoneNumber}</Link>
                                </HStack>
                            )}
                            {candidate.email && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiEnvelope /></Box>
                                    <Link href={`mailto:${candidate.email}`} color="white" fontSize="sm">{candidate.email}</Link>
                                </HStack>
                            )}
                            <HStack gap={2}>
                                <Box color="text.grey" fontSize="sm" flexShrink={0}><HiClock /></Box>
                                <Text color="text.grey" fontSize="sm">
                                    {t("candidateDetailPage.fieldStartAt")}:{" "}
                                    <Text as="span" color="white">{dayjs(candidate.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                                </Text>
                            </HStack>
                            {candidate.updatedAt && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiArrowPath /></Box>
                                    <Text color="text.grey" fontSize="sm">
                                        {t("candidateDetailPage.fieldLastActivity")}:{" "}
                                        <Text as="span" color="white">{dayjs(candidate.updatedAt).format("DD.MM.YYYY HH:mm")}</Text>
                                    </Text>
                                </HStack>
                            )}
                            {latestConsent !== undefined && (
                                <HStack gap={2}>
                                    <Box color={latestConsent ? "green.400" : "text.grey"} fontSize="sm" flexShrink={0}><HiOutlineShieldCheck /></Box>
                                    <Text color="text.grey" fontSize="sm">
                                        {t("candidateDetailPage.fieldConsents")}:{" "}
                                        <Text as="span" color={latestConsent ? "green.400" : "white"}>
                                            {latestConsent
                                                ? t("candidateDetailPage.consentsGiven", { date: dayjs(latestConsent.acceptedAt).format("DD.MM.YYYY HH:mm") })
                                                : t("candidateDetailPage.consentsMissing")}
                                        </Text>
                                    </Text>
                                </HStack>
                            )}
                        </VStack>
                        {/* Right: action buttons */}
                        <HStack gap={2} flexWrap="wrap" justify="flex-end">
                            <Button variant="outlined" size="sm" onClick={handleHeaderAddNote}>
                                {t("candidateDetailPage.addNote")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="sm"
                                disabled={!candidate.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.email)}
                                onClick={() => openEmailModal(candidateId)}
                            >
                                {t("candidateDetailPage.sendMessage")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="sm"
                                loading={isXlsxLoading}
                                disabled={!recruitmentId}
                                onClick={handleExportXlsx}
                            >
                                {t("candidateDetailPage.downloadXlsx")}
                            </Button>
                        </HStack>
                    </HStack>
                </Box>

                {/* AI Score card */}
                {SCORING_ENABLED && score != null && (
                    <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                        <HStack gap={6} align="center">
                            <Box position="relative" w="72px" h="72px" flexShrink={0}>
                                <svg viewBox="0 0 72 72" width="72" height="72">
                                    <circle cx="36" cy="36" r="28" fill="none" stroke="#313131" strokeWidth="5" />
                                    <circle
                                        cx="36" cy="36" r="28"
                                        fill="none"
                                        stroke={scoreRingColor}
                                        strokeWidth="5"
                                        strokeDasharray={SCORE_CIRC}
                                        strokeDashoffset={scoreOffset}
                                        strokeLinecap="round"
                                        transform="rotate(-90 36 36)"
                                    />
                                </svg>
                                <Box
                                    position="absolute"
                                    inset={0}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Text color="white" fontWeight={700} fontSize="lg" lineHeight={1}>
                                        {Math.round(score)}
                                    </Text>
                                    <Text color="text.grey" fontSize="2xs">/100</Text>
                                </Box>
                            </Box>
                            <VStack align="flex-start" gap={2} flex={1}>
                                <ScoreBadge evaluation={{ scorePercent: score, status: evaluationStatus }} size="sm" />
                                <HStack gap={3} w="full" align="center">
                                    <Box h="8px" bg="bg.secondary" borderRadius="full" flex={1} overflow="hidden">
                                        <Box
                                            h="full"
                                            bg={scoreBucketColor}
                                            w={`${Math.round(score)}%`}
                                            borderRadius="full"
                                            transition="width 0.4s ease"
                                        />
                                    </Box>
                                    <Text color="text.grey" fontSize="xs" flexShrink={0}>{Math.round(score)}%</Text>
                                </HStack>
                            </VStack>
                        </HStack>
                    </Box>
                )}

                {/* Tabs */}
                <Box w="full">
                    <HStack gap={0} borderBottom="1px solid" borderColor="border.primary" w="full">
                        {TABS.map(tab => (
                            <Box
                                key={tab.key}
                                as="button"
                                display="flex"
                                alignItems="center"
                                gap="6px"
                                px={4}
                                py={3}
                                cursor="pointer"
                                color={activeTab === tab.key ? "white" : "text.grey"}
                                borderBottomWidth="2px"
                                borderBottomStyle="solid"
                                borderBottomColor={activeTab === tab.key ? "brand.400" : "transparent"}
                                fontWeight={activeTab === tab.key ? 600 : 400}
                                fontSize="sm"
                                onClick={() => setActiveTab(tab.key)}
                                transition="all 0.15s"
                                mb="-1px"
                                bg="transparent"
                                outline="none"
                                _hover={{ color: "white" }}
                                userSelect="none"
                            >
                                <Text as="span" fontSize="sm" fontWeight="inherit">{tab.label}</Text>
                                {tab.count > 0 && (
                                    <Badge
                                        size="xs"
                                        variant="subtle"
                                        colorPalette={activeTab === tab.key ? "blue" : "gray"}
                                        borderRadius={10}
                                        fontSize="2xs"
                                        minW="18px"
                                        textAlign="center"
                                    >
                                        {tab.count}
                                    </Badge>
                                )}
                            </Box>
                        ))}
                    </HStack>

                    <Box pt={4}>
                        {SCORING_ENABLED && activeTab === "evaluation" && (
                            <CandidateEvaluationTab candidateId={candidateId} />
                        )}
                        {activeTab === "ai" && (
                            <CandidateAiDataTab candidate={candidate} onOpenDocument={handleOpenAttachment} />
                        )}
                        {activeTab === "timeline" && (
                            <CandidateTimelineTab timeline={timeline} isLoading={isTimelineLoading} candidateId={candidateId} onAddEntry={() => openTimelineModal(candidateId)} highlightId={highlightId} onHighlightClear={() => setHighlightId(null)} />
                        )}
                        {activeTab === "notes" && (
                            <CandidateNotesTab
                                notes={notes}
                                isLoading={isNotesLoading}
                                onAddNote={handleHeaderAddNote}
                                candidateId={candidateId}
                                search={notesSearch}
                                onSearchChange={setNotesSearch}
                                sortDirection={notesSortDirection}
                                onToggleSortDirection={() => setNotesSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                                highlightId={highlightId}
                                onHighlightClear={() => setHighlightId(null)}
                            />
                        )}
                        {activeTab === "attachments" && (
                            <CandidateAttachmentsTab
                                attachments={attachments}
                                isLoading={isAttachmentsLoading}
                                onOpenAttachment={handleOpenAttachment}
                                onDownloadAttachment={handleDownloadAttachment}
                                onUploadAttachments={handleUploadAttachments}
                                onDownloadAllZip={handleDownloadAllZip}
                            />
                        )}
                        {activeTab === "interactions" && (
                            <CandidateInteractionsTab candidateId={candidateId} />
                        )}
                    </Box>
                </Box>
            </VStack>

            {singleActionModals}
        </>
    );
};

export default CandidateDetailPage;
